import { useUploadFile } from "@/src/hooks/features/use-upload-file";
import { PickedFile } from "@/src/models/file.models";
import {
  ArticleInput,
  AttachmentInput,
  MergedAttachment,
  PresignedUploadResponse,
  UploadAttachmentResult,
  WikiFormData,
} from "@/src/models/wiki.models";
import { getToken } from "@/src/services/api/auth.api";
import apiClient from "@/src/services/api/axios";
import { Platform } from "react-native";
import gqlClient from "@/src/services/api/graphql.client";
import { CREATE_ARTICLE, UPDATE_ARTICLE } from "@/src/services/wiki/wiki.query";
import {
  buildKbText,
  filterValidKbFileIds,
  getAttachmentMeta,
  isRemoteFile,
  normalizeExistingAttachment,
  resolveFileName,
} from "@/src/utils/wiki.utils";

const BLOCKS_AI_URL = process.env.EXPO_PUBLIC_BLOCKS_BASE_URL;
const BASE_URL = process.env.EXPO_PUBLIC_BLOCKS_BASE_URL;
const PROJECT_KEY = process.env.EXPO_PUBLIC_X_BLOCKS_KEY as string;
const X_BLOCKS_KEY = process.env.EXPO_PUBLIC_X_BLOCKS_KEY as string;
const PARENT_DIRECTORY_ID = "8e7a9158-bbaf-4cb1-8592-35e677335c88";

export const gqlRequest = async (
  query: string,
  variables?: object,
): Promise<unknown> => {
  const client = await gqlClient();
  return client.request(query, variables);
};

export const uploadAttachmentWithFileId = async (
  uri: string,
  mimeType: string,
  fileName: string,
): Promise<UploadAttachmentResult> => {
  const isWeb = Platform.OS === "web";
  const token = await getToken();
  if (!token && !isWeb) throw new Error("No auth token available.");

  const presignedRes = await fetch(
    `${BASE_URL}/uds/v1/Files/GetPreSignedUrlForUpload`,
    {
      method: "POST",
      credentials: isWeb ? "include" : "omit",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "x-blocks-key": X_BLOCKS_KEY,
      },
      body: JSON.stringify({
        metaData: "",
        name: fileName,
        parentDirectoryId: PARENT_DIRECTORY_ID,
        tags: "",
        accessModifier: "Public",
        configurationName: "Default",
        projectKey: X_BLOCKS_KEY,
        moduleName: 3,
      }),
    },
  );

  if (!presignedRes.ok) {
    throw new Error(`GetPreSignedUrlForUpload failed: ${presignedRes.status}`);
  }

  const { uploadUrl, fileId } =
    (await presignedRes.json()) as PresignedUploadResponse;

  const fileRes = await fetch(uri);
  const blob = await fileRes.blob();
  const uploadRes = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": mimeType,
      "x-ms-blob-type": "BlockBlob",
    },
    body: blob,
  });

  if (uploadRes.status !== 201) throw new Error("File upload to blob failed");

  return {
    url: uploadUrl.split("?")[0],
    fileId,
  };
};

export const uploadLocalAttachments = async (
  localFiles: AttachmentInput[],
): Promise<MergedAttachment[]> =>
  Promise.all(
    localFiles.map(async (file, index) => {
      const { uri, mimeType, rawFileName } = getAttachmentMeta(file, index);
      const fileName = resolveFileName(uri, rawFileName, mimeType);
      const { url, fileId } = await uploadAttachmentWithFileId(
        uri,
        mimeType,
        fileName,
      );
      return {
        id: fileId,
        name: rawFileName,
        size:
          (file as { size?: number | string }).size != null
            ? String((file as { size?: number | string }).size)
            : "0",
        url,
        mimeType,
      };
    }),
  );

export const buildMergedAttachments = async (
  attachments: PickedFile[],
): Promise<MergedAttachment[]> => {
  const remoteAttachments = attachments.filter(isRemoteFile);
  const localAttachments = attachments.filter(
    (file) => !isRemoteFile(file),
  ) as AttachmentInput[];

  const normalizedRemote = remoteAttachments.map(normalizeExistingAttachment);
  const uploadedLocal = await uploadLocalAttachments(localAttachments);

  return [...normalizedRemote, ...uploadedLocal];
};

export const uploadCoverImageIfLocal = async (
  coverImage: PickedFile | null,
  uploadImage: ReturnType<typeof useUploadFile>["mutateAsync"],
): Promise<string> => {
  const uri = (coverImage as { uri?: string } | null)?.uri ?? "";
  if (!uri || uri.startsWith("http")) return uri;

  const mimeType = (coverImage as { mimeType?: string } | null)?.mimeType;
  const rawFileName = (coverImage as { fileName?: string } | null)?.fileName;

  return uploadImage({
    uri,
    mimeType: mimeType ?? "application/octet-stream",
    fileName: resolveFileName(uri, rawFileName ?? null, mimeType ?? null),
  } as Parameters<typeof uploadImage>[0]);
};

export const saveArticleToGraphQL = async (
  data: WikiFormData,
  coverImageUrl: string,
  mergedAttachments: MergedAttachment[],
  categoryId: string,
  isDraft: boolean,
  articleInput: ArticleInput,
  isEditing: boolean,
  draftId: string | undefined,
): Promise<string> => {
  if (isEditing) {
    await gqlRequest(UPDATE_ARTICLE, {
      filter: JSON.stringify({ ItemId: draftId }),
      input: articleInput,
    });
    return draftId!;
  }

  const result = (await gqlRequest(CREATE_ARTICLE, {
    input: articleInput,
  })) as { insertwiki?: { itemId: string } };

  return result?.insertwiki?.itemId ?? "";
};

export const ingestTextToKnowledgeBase = async (
  title: string,
  content: string,
  kbFolderId: string,
): Promise<void> => {
  const kbText = buildKbText(title, content);
  await apiClient.post(`${BLOCKS_AI_URL}/blocksai-api/v1/kb/text`, {
    text: kbText,
    project_key: PROJECT_KEY,
    folder_id: kbFolderId,
  });
};

export const ingestFilesToKnowledgeBase = async (
  attachments: MergedAttachment[],
  kbFolderId: string,
): Promise<void> => {
  const validFileIds = filterValidKbFileIds(attachments);
  if (validFileIds.length === 0) return;

  await apiClient.post(`${BLOCKS_AI_URL}/blocksai-api/v1/kb/file`, {
    folder_id: kbFolderId,
    project_key: PROJECT_KEY,
    file_ids: validFileIds,
  });
};

export const ingestArticleToKnowledgeBase = async (
  title: string,
  content: string,
  attachments: MergedAttachment[],
  kbFolderId: string,
): Promise<void> => {
  await ingestTextToKnowledgeBase(title, content, kbFolderId);
  await ingestFilesToKnowledgeBase(attachments, kbFolderId);
};
